import { _ } from "libs";

import React, { useState, useRef } from "react";
import { Profile } from "../../types";
import { DropzoneRef, useDropzone } from "react-dropzone";
import Cropper, { Area } from "react-easy-crop";
import Avatar from "../avatar";

const createImage = (url: string) =>
	new Promise((resolve, reject) => {
		const image = new Image();
		image.addEventListener("load", () => resolve(image));
		image.addEventListener("error", error => reject(error));
		image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
		image.src = url;
	});

async function getCroppedImg(src: string, pixelCrop: Area) {
	const image = (await createImage(src)) as CanvasImageSource;
	const canvas = document.createElement("canvas");
	canvas.width = pixelCrop.width;
	canvas.height = pixelCrop.height;
	const ctx = canvas.getContext("2d");

	ctx.drawImage(
		image,
		pixelCrop.x,
		pixelCrop.y,
		pixelCrop.width,
		pixelCrop.height,
		0,
		0,
		pixelCrop.width,
		pixelCrop.height
	);

	// As Base64 string
	return canvas.toDataURL("image/jpeg");
}

type Props = {
	profile: Profile;
	onAvatarCropped: (image: string) => void;
	onAvatarSkip: Function;
	onContinue: () => void;
	onAvatarRemove: () => void;
};

export function Step2({ profile, onAvatarCropped, onAvatarSkip, onAvatarRemove, onContinue }: Props) {
	const [src, setSrc] = useState(null);
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [saving, setSaving] = useState(false);
	const [pixelCrop, setPixelCrop] = useState(null as Area);

	const dropzone = useRef<DropzoneRef>();
	const input = useRef<HTMLInputElement>();

	const reader = new FileReader();
	reader.onabort = () => console.log("file reading was aborted");
	reader.onerror = () => console.log("file reading has failed");
	reader.onload = () => {
		setSrc(reader.result);
	};

	const openDialog = () => {
		// Note that the ref is set async,
		// so it might be null at some point
		if (dropzone.current) {
			//@ts-ignore
			dropzone.current.open();
		}
	};

	const onDrop = (acceptedFiles: File[]) => {
		acceptedFiles.forEach(file => reader.readAsDataURL(file));
	};

	const onCropComplete = (croppedArea: Area, pixelCrop: Area) => {
		setPixelCrop(pixelCrop);
	};

	const onSave = async () => {
		setSaving(true);

		const image = await getCroppedImg(src, pixelCrop);
		//setSaving(false);
		onAvatarCropped(image);
	};

	const removeSrc = () => {
		setSrc(null);
	};

	const { getRootProps, getInputProps, open, acceptedFiles, isDragActive } = useDropzone({
		//noClick: true,
		onDrop,
		accept: ["image/png", "image/jpeg"],
		multiple: false,
	});

	return (
		<div className="box onboardingStep2">
			<div className="boxLeader">
				<h2>{_("onboarding.profilePicture.heading")}</h2>
				<div className="illustration player_waiting" />
			</div>
			<div className="boxContent">
				{src ? (
					<>
						<div className="profilePictureBox">
							<a className="remove" onClick={() => removeSrc()} />
							<div className="cropperWrapper">
								<Cropper
									cropShape={"round"}
									showGrid={false}
									image={src}
									crop={crop}
									zoom={zoom}
									aspect={1}
									onCropChange={setCrop}
									onCropComplete={onCropComplete}
									onZoomChange={setZoom}
								/>
							</div>
						</div>
						<button className={`button done ${saving ? "loading" : ""}`} onClick={onSave} disabled={saving}>
							{_("continueBtn")}
						</button>
					</>
				) : // Does user already have a profile picture?
				profile.photoUrl && !profile.photoUrl.includes("default_player") ? (
					<>
						<div className="profilePictureBox">
							<Avatar photoUrl={profile.photoUrl} />
							<a className="remove" onClick={onAvatarRemove} />
						</div>
						<button className="button done fullWidth" onClick={onContinue}>
							{_("continueBtn")}
						</button>
					</>
				) : (
					<section {...getRootProps()}>
						<input {...getInputProps()} ref={input} />
						{isDragActive ? (
							<div className="profilePictureBox dropContainer">
								<p>{_("onboarding.profilePicture.dropzoneText")}</p>
							</div>
						) : (
							<div className="profilePictureBox">
								<Avatar photoUrl={profile.photoUrl} />
								<a className="link blue" onClick={() => onAvatarSkip()}>
									{_("onboarding.profilePicture.useDefaultAvatarBtn")}
								</a>
							</div>
						)}
						<button
							className="button done fullWidth"
							onClick={() => {
								//console.log("clicking open");
								//open();
								input.current.click();
							}}
						>
							{_("onboarding.profilePicture.chooseFileBtn")}
						</button>
					</section>
				)}
			</div>
		</div>
	);
}

export default Step2;
